import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Undo2 } from 'lucide-react';

/**
 * Ephemeral "pages moved" pill with an Undo action and a closing-circle
 * countdown. After a page reorder, this shows which pages moved and offers an
 * Undo until the timer expires (default 15s), then auto-dismisses.
 *
 * The countdown is a stroke-dashoffset animation on an SVG circle, so it reads
 * as a ring that "closes" (empties) over the window — a cheap, GPU-friendly
 * timer with no per-frame React state.
 */

const RING_SIZE = 20;
const RING_STROKE = 2.5;
const RING_R = (RING_SIZE - RING_STROKE) / 2;
const RING_C = 2 * Math.PI * RING_R;

const Pill = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 6px 10px 6px 8px;
    border-radius: 999px;
    background: #111827;
    color: #f9fafb;
    font-size: 13px;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18);
`;

const Ring = styled.svg`
    flex-shrink: 0;
    transform: rotate(-90deg); /* start the sweep at 12 o'clock */
`;

// Drain the ring over the countdown window. Animated via inline style so the
// duration is data-driven; the keyframe just goes full -> empty.
const drain = keyframes`
    from { stroke-dashoffset: 0; }
    to   { stroke-dashoffset: ${RING_C}; }
`;

const RingTrack = styled.circle`
    fill: none;
    stroke: rgba(255, 255, 255, 0.18);
    stroke-width: ${RING_STROKE};
`;

const RingProgress = styled.circle<{ $durationMs: number }>`
    fill: none;
    stroke: #93c5fd;
    stroke-width: ${RING_STROKE};
    stroke-linecap: round;
    stroke-dasharray: ${RING_C};
    animation: ${drain} ${({ $durationMs }) => $durationMs}ms linear forwards;
`;

const Label = styled.span`
    white-space: nowrap;
`;

const UndoButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border: none;
    background: transparent;
    color: #93c5fd;
    font: inherit;
    font-weight: 600;
    cursor: pointer;
    padding: 2px 4px;
    border-radius: 6px;

    &:hover { background: rgba(255, 255, 255, 0.08); color: #bfdbfe; }
    &:focus-visible { outline: 2px solid #93c5fd; outline-offset: 1px; }
`;

export interface PageMovePillProps {
    /** Human description of what moved, e.g. "Moved pages 3–5". */
    label: string;
    /** Called when the user clicks Undo. */
    onUndo: () => void;
    /** Called when the countdown expires or the pill is otherwise dismissed. */
    onExpire: () => void;
    /** Countdown window in ms (default 15000). */
    durationMs?: number;
}

export const PageMovePill: React.FC<PageMovePillProps> = ({
    label,
    onUndo,
    onExpire,
    durationMs = 15000,
}) => {
    // Fire onExpire once when the window elapses. Keeping the timer here (not in
    // the parent) means the parent only has to mount/unmount the pill.
    const onExpireRef = React.useRef(onExpire);
    onExpireRef.current = onExpire;
    React.useEffect(() => {
        const t = window.setTimeout(() => onExpireRef.current(), durationMs);
        return () => window.clearTimeout(t);
    }, [durationMs]);

    return (
        <Pill role="status" aria-live="polite">
            <Ring width={RING_SIZE} height={RING_SIZE} aria-hidden>
                <RingTrack cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={RING_R} />
                <RingProgress
                    cx={RING_SIZE / 2}
                    cy={RING_SIZE / 2}
                    r={RING_R}
                    $durationMs={durationMs}
                />
            </Ring>
            <Label>{label}</Label>
            <UndoButton type="button" onClick={onUndo}>
                <Undo2 size={14} />
                Undo
            </UndoButton>
        </Pill>
    );
};

export default PageMovePill;
